import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { injectVfsTimesNewRoman } from "../functions/pdfMake/timesNewRomanFonts";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
injectVfsTimesNewRoman(pdfMake.vfs);

// Важно: pdfMake должен знать соответствие "имя шрифта" -> файлы в vfs,
// иначе при bold/italics падает с ошибкой "Font 'TimesNewRoman' ... is not defined".
pdfMake.fonts = pdfMake.fonts || {};
pdfMake.fonts.TimesNewRoman = {
    normal: "Times-Regular.ttf",
    bold: "Times-Bold.ttf",
    italics: "Times-Italic.ttf",
    bolditalics: "Times-BoldItalic.ttf",
};

const toLines = (value) => {
    if (value == null) return [];
    const s = String(value).replace(/\r\n/g, "\n").trim();
    if (!s) return [];
    return s.split("\n").map(l => l.trimEnd());
};

const toDashedLines = (value) => {
    const lines = toLines(value);
    return lines
        .filter(l => l.trim() !== "")
        .map(l => {
            const trimmed = l.trim();
            // если уже начинается с "-" или "—", оставляем как есть
            if (/^[-—]\s*/.test(trimmed)) return trimmed;
            return `- ${trimmed}`;
        });
};

const asDateRu = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("ru-RU");
    } catch (e) {
        return String(iso);
    }
};

const safe = (v) => (v == null || String(v).trim() === "" ? "" : String(v));
const formatText = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (Array.isArray(v)) return v.map(i => (typeof i === "string" ? i : JSON.stringify(i))).join("\n");
    if (typeof v === "object") return JSON.stringify(v, null, 2);
    return String(v);
};

export default class ReportPdfModel {
    buildDocDefinition(report) {
        const filialLabel =
            report?.filial?.name ||
            (report?.filialId != null ? `ID: ${report.filialId}` : "");
        const filialRegionTitleRaw = report?.filialRegionLabel || filialLabel || "";
        const filialRegionTitle = String(filialRegionTitleRaw).replace(/^\s*по\s+/i, "").trim();
        // Переводим в родительный падеж: "Филиал ..." -> "Филиала ..."
        let filialGenitiveTitle = "";
        if (filialRegionTitle) {
            const trimmed = filialRegionTitle.trim();
            if (/филиал/i.test(trimmed)) {
                filialGenitiveTitle = trimmed.replace(/филиал/i, "Филиала");
            } else {
                filialGenitiveTitle = `Филиала ${trimmed}`;
            }
        }

        const assessmentDate = asDateRu(report?.assessmentDate);

        const npaLines = toDashedLines(report?.npaLink);
        const abbrLines = toLines(report?.abbreviations);

        const labsText = formatText(report?.laboratories);
        const expertsText = formatText(report?.experts);

        const content = [
            {
                text: "ОТЧЕТ",
                style: "title",
                alignment: "center",
                margin: [0, 0, 0, 4],
            },
            {
                text: `«Оценка состояния биологической безопасности и защиты в лабораториях\n${filialGenitiveTitle}`,
                style: "title",
                alignment: "center",
                margin: [0, 0, 0, 4],
            },
            {
                table: {
                    widths: [140, "*"],
                    body: [
                        ["ОРГАНИЗАЦИЯ", safe(report?.generalInformation) ? safe(filialLabel) : safe(filialLabel)],
                        ["ЛАБОРАТОРИИ", labsText],
                        ["ДАТА ОЦЕНКИ", assessmentDate],
                        ["ЭКСПЕРТЫ", expertsText],
                        ["ССЫЛКА НА НПА", npaLines.length ? { text: npaLines.join("\n") } : ""],
                    ],
                },
                layout: {
                    hLineWidth: () => 0.7,
                    vLineWidth: () => 0.7,
                    hLineColor: () => "#000",
                    vLineColor: () => "#000",
                    paddingLeft: () => 4,
                    paddingRight: () => 4,
                    paddingTop: () => 2,
                    paddingBottom: () => 2,
                },
                style: "table",
            },
            { text: "\nСОКРАЩЕНИЯ", style: "h2" },
            abbrLines.length ? { text: abbrLines.join("\n"), style: "body" } : { text: "", style: "body" },
            { text: "\nВВЕДЕНИЕ", style: "h2" },
            { text: safe(report?.introduction), style: "body" },
            { text: "\nОБЩАЯ ИНФОРМАЦИЯ ПО ЛАБОРАТОРИЯМ", style: "h2" },
            { text: safe(report?.generalInformation), style: "body" },
            { text: "\nОПИСАНИЕ ИНСТРУМЕНТОВ ОЦЕНКИ ЛАБОРАТОРИЙ", style: "h2" },
            { text: safe(report?.toolsDescription), style: "body" },
            { text: "\nРЕЗУЛЬТАТЫ", style: "h2" },
            { text: safe(report?.results), style: "body" },
            { text: "\nЗАКЛЮЧЕНИЕ", style: "h2" },
            { text: safe(report?.conclusion), style: "body" },
            { text: "\nРЕКОМЕНДАЦИИ", style: "h2" },
            { text: safe(report?.recommendations), style: "body" },
        ];

        return {
            pageSize: "A4",
            pageMargins: [40, 40, 40, 40],
            defaultStyle: {
                font: "TimesNewRoman",
                fontSize: 12,
            },
            styles: {
                title: { fontSize: 14, bold: true },
                subtitle: { fontSize: 12, italics: true },
                h2: { fontSize: 12, bold: true, margin: [0, 8, 0, 4] },
                body: { fontSize: 12, lineHeight: 1.15 },
                table: { fontSize: 11 },
            },
            content,
        };
    }

    getDataUrl(report) {
        const doc = this.buildDocDefinition(report);
        return new Promise((resolve, reject) => {
            let done = false;
            const timer = setTimeout(() => {
                if (done) return;
                done = true;
                reject(new Error("pdfMake.getDataUrl timeout"));
            }, 15000);

            try {
                pdfMake.createPdf(doc).getDataUrl((dataUrl) => {
                    if (done) return;
                    done = true;
                    clearTimeout(timer);
                    resolve(dataUrl);
                });
            } catch (e) {
                if (done) return;
                done = true;
                clearTimeout(timer);
                reject(e);
            }
        });
    }

    download(report, fileName) {
        const doc = this.buildDocDefinition(report);
        const name = fileName || `IQLS_отчет_${report?.id || "new"}.pdf`;
        pdfMake.createPdf(doc).download(name);
    }
}

